import { Text } from "@/elements"
import { TextVariant } from "@/theme"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"

interface CollapsibleTextProps {
  text: string
  maxLines?: number
  className?: string
  textVariant?: TextVariant
  moreLessTextVariant?: TextVariant
}

export const CollapsibleText: React.FC<CollapsibleTextProps> = ({
  text,
  maxLines = 2,
  textVariant = "body1",
  moreLessTextVariant = "body1",
  className,
}) => {
  const [expanded, setExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isClamped, setClamped] = useState(false)
  const [modifiedText, setModifiedText] = useState<string | undefined>()
  const measuringRef = useRef<HTMLDivElement>(null)

  const textMeasureRef = useRef<HTMLDivElement>(null)

  const toggleExpanded = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()
    e.preventDefault()
    setExpanded(prev => !prev)
  }
  useEffect(() => {
    // Function that should be called on window resize
    function handleResize() {
      if (contentRef.current && measuringRef.current && textMeasureRef.current) {
        measuringRef.current.innerText = ""
        if (contentRef.current.scrollHeight > contentRef.current.clientHeight) {
          setClamped(true)

          const words = text.replace(/(\S)(\n)/g, "$1 $2").split(" ")
          let left = 0
          let right = words.length
          let result = ""

          while (left < right) {
            const mid = Math.floor((left + right) / 2)
            const testText = words.slice(0, mid).join(" ") + " ...More"
            measuringRef.current.innerText = testText

            if (measuringRef.current.scrollHeight <= contentRef.current.clientHeight) {
              result = words.slice(0, mid).join(" ")
              left = mid + 1
            } else {
              right = mid
            }
          }

          setModifiedText(result)
        } else {
          setClamped(false)
          setModifiedText(text)
        }
      }
    }

    handleResize()

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [text])

  return (
    <div className={clsx("relative w-full", className)}>
      <Text
        ref={contentRef}
        variant={textVariant}
        style={{
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "none" : maxLines,
        }}
        className={clsx("absolute left-0 top-0 inline w-full whitespace-pre-wrap text-red-default opacity-0")}>
        {text}
      </Text>
      <Text
        style={{
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "none" : maxLines,
        }}
        variant={textVariant}
        className={clsx("relative inline whitespace-pre-wrap")}>
        {!expanded ? modifiedText : text}
        {isClamped && !expanded && (
          <Text
            element="span"
            variant={moreLessTextVariant}
            onClick={toggleExpanded}
            className="relative bottom-0 right-0 z-10 cursor-pointer whitespace-pre-wrap bg-bg-1 text-primary-3 hover:text-fg-1">
            {" "}
            ...more
          </Text>
        )}
      </Text>
      <Text
        id="measureRef"
        variant={textVariant}
        className="absolute left-0 top-0 w-full opacity-0"
        style={{
          display: "block",
          wordWrap: "break-word",
          whiteSpace: "normal",
        }}
        ref={measuringRef}
      />
      <Text
        id="textMeasure"
        variant={textVariant}
        className="absolute left-0 top-0 w-fit opacity-0"
        style={{
          display: "block",
          wordWrap: "break-word",
          whiteSpace: "normal",
        }}
        ref={textMeasureRef}
      />
      {expanded && (
        <Text
          onClick={toggleExpanded}
          variant={moreLessTextVariant}
          className="w-fit cursor-pointer text-primary-3 hover:text-fg-1">
          less
        </Text>
      )}
    </div>
  )
}
